import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { StatsCard } from '../components/ui/stats-card';
import { SegmentedSwitch } from '../components/ui/segmented-switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Download,
  Filter,
  ArrowRight,
  Pencil,
  X,
  Loader2,
  CheckCircle2,
  XCircle,
  Upload,
  Image as ImageIcon,
  Eye,
  ChevronDown,
  ChevronRight,
  FolderTree,
  List,
  FolderKanban,
  GitBranch,
  ShoppingBag,
  Calculator,
  MapPin,
  MoreHorizontal,
  Settings2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { CategoryAPI, ProductAPI, Product } from '../lib/api';
import { toast } from '../components/ui/use-toast';
import { API_BASE_URL, getImageUrl } from '../lib/config';
import { ImageUpload } from '../components/ui/image-upload';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { cn } from '../lib/utils';
import { PricingConfigModal } from '../components/categories/PricingConfigModal';


// Helper function to format image URL
const formatImageUrl = (imagePath: string | null | undefined): string => {
  if (!imagePath) return '';
  return getImageUrl(imagePath);
};

// Define API response type for GetCategory
interface CategoryResponse {
  category: Category;
  subcategories: Category[];
}

// Category interface updated to match API response
interface Category {
  _id: string;
  name: string;
  description: string;
  image: string;
  isActive: boolean;
  parent: string | null;
  level: number;
  useCalculationLogic: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  id?: string;
  __v?: number;
  // For UI organization - these won't come from API
  subcategories?: Category[];
  expanded?: boolean;
  // Add productCount from the API
  productCount?: number;
}

// Pagination interface
interface PaginatedProductsResponse {
  products: Product[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  }
}

// Sample data for categories
const sampleCategoryData: Category[] = [
  {
    _id: "1",
    name: 'Sand',
    description: 'Different varieties of construction sand',
    image: "",
    isActive: true,
    parent: null,
    level: 1,
    useCalculationLogic: true,
    createdBy: "user1",
    createdAt: '2023-01-15T00:00:00.000Z',
    updatedAt: '2023-01-15T00:00:00.000Z',
    productCount: 8
  },
  // ... other sample categories
];

// Inline components removed in favor of imported UI components

export default function CategoriesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewSubcategoriesModalOpen, setIsViewSubcategoriesModalOpen] = useState(false);
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    parent: '',
    useCalculationLogic: true
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [editImage, setEditImage] = useState<File | null>(null);
  const [viewMode, setViewMode] = useState<'hierarchy' | 'all'>('hierarchy');
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);


  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await CategoryAPI.getAllCategories();
      // Check if response is in expected format
      if (response.success && Array.isArray(response.data)) {
        // Process categories to build hierarchy
        const categories = response.data;

        // If we're in hierarchy view, organize them
        if (viewMode === 'hierarchy') {
          // Get parent categories (level 1)
          const parentCategories = categories.filter(cat => cat.level === 1);

          // Assign subcategories to their parents
          parentCategories.forEach(parent => {
            // Find all subcategories for this parent
            const subcats = categories.filter(cat => cat.parent === parent._id);

            // Use type assertion to tell TypeScript that these properties exist
            (parent as any).subcategories = subcats;
            (parent as any).expanded = false;
          });

          setCategories(parentCategories);
        } else {
          // Flat view - just set all categories
          setCategories(categories);
        }
      } else {
        console.warn('API response format unexpected, using fallback data', response);
        // Fallback to sample data if API format is unexpected
        setCategories(sampleCategoryData);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again later.');
      // Fallback to sample data on error
      setCategories(sampleCategoryData);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryDetails = async (categoryId: string) => {
    setLoading(true);
    try {
      const response = await CategoryAPI.getCategory(categoryId);

      if (response.success) {
        // Check if response has expected structure
        if ('category' in response.data && 'subcategories' in response.data) {
          // API returns { category, subcategories } structure
          const categoryResponse = response.data as unknown as CategoryResponse;
          setSubcategories(categoryResponse.subcategories);
          setSelectedCategory(categoryResponse.category);
          return categoryResponse.subcategories;
        } else {
          // Fallback if the API returns a different structure
          // Assuming response.data might be the category itself
          setSelectedCategory(response.data);
          // Check if it has subcategories property
          const categoryWithSubs = response.data as Category & { subcategories?: Category[] };
          if (categoryWithSubs.subcategories) {
            setSubcategories(categoryWithSubs.subcategories);
            return categoryWithSubs.subcategories;
          } else {
            setSubcategories([]);
            return [];
          }
        }
      } else {
        setSubcategories([]);
        return [];
      }
    } catch (err) {
      console.error('Error fetching category details:', err);
      toast({
        title: "Error",
        description: "Failed to load subcategories. Please try again.",
        variant: "destructive",
      });
      setSubcategories([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Filter categories based on debounced search query
  const getFilteredCategories = useCallback(() => {
    let filtered = categories;

    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = categories.filter(category => {
        const matchesSearch =
          category.name.toLowerCase().includes(query) ||
          category.description.toLowerCase().includes(query);

        if (matchesSearch) return true;

        // Check subcategories if we're in hierarchy view
        if (viewMode === 'hierarchy' && category.subcategories) {
          const hasMatchingSubcategory = category.subcategories.some(sub =>
            sub.name.toLowerCase().includes(query) ||
            sub.description.toLowerCase().includes(query)
          );
          return hasMatchingSubcategory;
        }

        return false;
      });
    }

    return filtered;
  }, [categories, debouncedSearchQuery, viewMode]);

  const toggleCategoryExpansion = (categoryId: string) => {
    setCategories(prev => prev.map(category => {
      if (category._id === categoryId) {
        return { ...category, expanded: !category.expanded };
      }
      return category;
    }));
  };

  // Calculate the total products count in a category (including subcategories)
  const getTotalProductsCount = (category: Category): number => {
    const ownCount = category.productCount || 0;
    const subcategoriesCount = category.subcategories?.reduce(
      (total, sub) => total + (sub.productCount || 0), 0
    ) || 0;
    return ownCount + subcategoriesCount;
  };

  // Format dates for consistency
  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Expand all categories
  const expandAllCategories = () => {
    setCategories(prev => prev.map(category => ({
      ...category,
      expanded: true
    })));
  };

  // Collapse all categories
  const collapseAllCategories = () => {
    setCategories(prev => prev.map(category => ({
      ...category,
      expanded: false
    })));
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Category name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const result = await CategoryAPI.createCategory({
        name: newCategory.name,
        description: newCategory.description,
        parent: newCategory.parent || undefined,
        useCalculationLogic: newCategory.useCalculationLogic
      }, selectedImage || undefined);

      // Refresh categories list
      await fetchCategories();

      // Reset form and close modal
      setIsAddModalOpen(false);
      setNewCategory({ name: '', description: '', parent: '', useCalculationLogic: true });
      setSelectedImage(null);

      toast({
        title: "Success",
        description: "Category created successfully!",
        variant: "default",
      });
    } catch (err) {
      console.error('Error creating category:', err);
      toast({
        title: "Error",
        description: "Failed to create category. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!selectedCategory) return;

    try {
      setLoading(true);
      await CategoryAPI.updateCategory(selectedCategory._id, {
        name: selectedCategory.name,
        description: selectedCategory.description,
        isActive: selectedCategory.isActive,
        parent: selectedCategory.parent || undefined,
        useCalculationLogic: selectedCategory.useCalculationLogic
      }, editImage || undefined);

      // Refresh categories list
      await fetchCategories();

      // Close modal
      setIsEditModalOpen(false);
      setEditImage(null);

      toast({
        title: "Success",
        description: "Category updated successfully!",
        variant: "default",
      });
    } catch (err) {
      console.error('Error updating category:', err);
      toast({
        title: "Error",
        description: "Failed to update category. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubcategories = async (category: Category) => {
    setSelectedCategory(category);
    await fetchCategoryDetails(category._id);
    setIsViewSubcategoriesModalOpen(true);
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      setLoading(true);
      await CategoryAPI.deleteCategory(selectedCategory._id);

      // Refresh categories list
      await fetchCategories();

      // Close modal
      setIsDeleteModalOpen(false);
      toast({
        title: "Success",
        description: "Category deleted successfully!",
        variant: "default",
      });
    } catch (err) {
      console.error('Error deleting category:', err);
      toast({
        title: "Error",
        description: "Failed to delete category. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCalculationLogic = async (category: Category) => {
    try {
      setLoading(true);
      await CategoryAPI.toggleCalculationLogic(category._id, !category.useCalculationLogic);

      // Refresh categories list
      await fetchCategories();

      toast({
        title: "Success",
        description: `Distance-based pricing ${!category.useCalculationLogic ? 'enabled' : 'disabled'} for ${category.name}`,
        variant: "default",
      });
    } catch (err) {
      console.error('Error updating calculation logic:', err);
      toast({
        title: "Error",
        description: "Failed to update calculation logic. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (category: Category) => {
    try {
      setLoading(true);
      // Use the dedicated toggle-active endpoint instead of updateCategory
      await CategoryAPI.toggleCategoryActive(category._id);

      // Refresh categories list
      await fetchCategories();

      toast({
        title: "Success",
        description: `Category ${category.isActive ? 'deactivated' : 'activated'} successfully!`,
        variant: "default",
      });
    } catch (err) {
      console.error('Error updating category status:', err);
      toast({
        title: "Error",
        description: "Failed to update category status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Open pricing config modal
  const handleOpenPricingConfig = (category: Category) => {
    if (!category.useCalculationLogic) {
      toast({
        title: "Not Available",
        description: "Enable dynamic pricing first to configure the pricing formula.",
        variant: "default",
      });
      return;
    }
    setSelectedCategory(category);
    setIsPricingModalOpen(true);
  };

  // Get all parent categories for dropdowns
  const getParentCategories = () => {
    return categories.filter(category => category.level === 1);
  };

  const renderCategoryRow = (category: Category, isSubcategory = false, depth = 0) => (
    <TableRow
      key={category._id}
      className={`group transition-colors hover:bg-muted/50 ${isSubcategory ? "bg-muted/20" : ""}`}
    >
      <TableCell className="py-3">
        <div className="relative h-12 w-12 rounded-lg overflow-hidden border bg-muted">
          {category.image ? (
            <img
              src={formatImageUrl(category.image)}
              alt={category.name}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
              <ImageIcon className="h-5 w-5 opacity-50" />
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {isSubcategory && (
            <div style={{ paddingLeft: `${depth * 16}px` }} className="flex items-center text-muted-foreground">
              <div className="h-px w-3 bg-border mr-2" />
            </div>
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-foreground/90">{category.name}</span>
              {category.level === 1 && viewMode === 'hierarchy' && category.subcategories && category.subcategories.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategoryExpansion(category._id);
                  }}
                >
                  {(category as any).expanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </Button>
              )}
            </div>
            {category.level === 1 && viewMode === 'hierarchy' && (
              <span className="text-xs text-muted-foreground">
                {category.subcategories?.length || 0} subcategories
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground line-clamp-1 max-w-[200px]" title={category.description}>
          {category.description || "-"}
        </span>
      </TableCell>
      <TableCell className="text-center">
        <Badge variant="outline" className="font-mono text-xs">
          Lvl {category.level}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatDate(category.createdAt)}
      </TableCell>
      <TableCell className="text-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewProducts(category)}
                className={cn(
                  "h-8 px-3 font-medium",
                  getTotalProductsCount(category) > 0 ? "text-primary hover:text-primary/80 hover:bg-primary/10" : "text-muted-foreground"
                )}
                disabled={getTotalProductsCount(category) === 0}
              >
                {getTotalProductsCount(category) || 0}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{getTotalProductsCount(category) === 0 ? "No products" : "View products"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell className="text-center">
        <div className="flex items-center justify-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleCalculationLogic(category)}
                  disabled={loading}
                  className={cn(
                    "h-8 px-2 text-xs gap-1.5 transition-all border",
                    category.useCalculationLogic
                      ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                      : "bg-muted/50 text-muted-foreground border-transparent hover:bg-muted"
                  )}
                >
                  {category.useCalculationLogic ? (
                    <>
                      <Calculator className="h-3.5 w-3.5" />
                      Dynamic
                    </>
                  ) : (
                    <>
                      <MapPin className="h-3.5 w-3.5" />
                      Fixed
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-xs">
                  {category.useCalculationLogic
                    ? "Distance-based pricing enabled. Click to disable."
                    : "Fixed pricing enabled. Click to enable dynamic pricing."
                  }
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Configure Pricing Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8 transition-all",
                    category.useCalculationLogic
                      ? "hover:bg-indigo-50 hover:text-indigo-600 text-indigo-500"
                      : "text-muted-foreground/40 cursor-not-allowed"
                  )}
                  onClick={() => handleOpenPricingConfig(category)}
                  disabled={loading || !category.useCalculationLogic}
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">
                  {category.useCalculationLogic
                    ? "Configure pricing formula"
                    : "Enable dynamic pricing to configure formula"
                  }
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch
            checked={category.isActive}
            onCheckedChange={() => handleToggleStatus(category)}
            disabled={loading}
            className="scale-75 data-[state=checked]:bg-green-600"
          />
          <Badge variant={category.isActive ? 'success' : 'secondary'} className="text-xs px-2 py-0.5">
            {category.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {category.level === 1 && viewMode === 'hierarchy' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-muted"
                    onClick={() => handleViewSubcategories(category)}
                  >
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>View Subcategories</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-blue-50 hover:text-blue-600"
                  onClick={() => {
                    setSelectedCategory(category);
                    setIsEditModalOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit Category</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-red-50 hover:text-red-600"
                  onClick={() => {
                    setSelectedCategory(category);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete Category</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderCategoryWithSubcategories = (category: Category, depth = 0) => {
    const rows = [renderCategoryRow(category, false, depth)];

    if ((category as any).expanded && category.subcategories && category.subcategories.length > 0) {
      category.subcategories.forEach(subcategory => {
        rows.push(renderCategoryRow(subcategory, true, depth + 1));
      });
    }

    return rows;
  };

  const filteredCategories = getFilteredCategories();

  // Calculate statistics
  const totalCategories = viewMode === 'hierarchy'
    ? categories.length + categories.reduce((total, cat) => total + (cat.subcategories?.length || 0), 0)
    : categories.length;

  const activeCategories = viewMode === 'hierarchy'
    ? categories.filter(c => c.isActive).length +
    categories.reduce((total, cat) =>
      total + (cat.subcategories?.filter(sub => sub.isActive)?.length || 0), 0)
    : categories.filter(c => c.isActive).length;

  const totalProducts = categories.reduce((total, cat) =>
    total + getTotalProductsCount(cat), 0);

  // New function to fetch products by category
  const fetchProductsByCategory = async (categoryId: string) => {
    setProductsLoading(true);
    try {
      const response = await ProductAPI.getProductsByCategory(categoryId);

      if (response.success) {
        // Check if the response has a paginated structure (data.products array)
        if (response.data && 'products' in response.data &&
          Array.isArray(response.data.products)) {
          setCategoryProducts(response.data.products);
        }
        // Check if the data is a direct array (old API structure)
        else if (Array.isArray(response.data)) {
          setCategoryProducts(response.data);
        }
        else {
          console.warn('Products API response structure unexpected:', response);
          setCategoryProducts([]);
        }
      } else {
        setCategoryProducts([]);
        toast({
          title: "Warning",
          description: "No products found for this category.",
          variant: "default",
        });
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setCategoryProducts([]);
      toast({
        title: "Error",
        description: "Failed to load products. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProductsLoading(false);
    }
  };

  // Function to handle opening products modal
  const handleViewProducts = async (category: Category) => {
    setSelectedCategory(category);
    setIsProductsModalOpen(true);
    await fetchProductsByCategory(category._id);
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-1 sm:px-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Categories</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Manage your product categories</p>
        </div>
        <div className="flex gap-2">
          <SegmentedSwitch
            options={[
              {
                value: 'hierarchy',
                label: 'Hierarchy',
                icon: <FolderTree className="h-4 w-4" />
              },
              {
                value: 'all',
                label: 'Flat View',
                icon: <List className="h-4 w-4" />
              }
            ]}
            value={viewMode}
            onValueChange={(value) => setViewMode(value as 'hierarchy' | 'all')}
          />
          <Button onClick={() => setIsAddModalOpen(true)} className="flex-1 sm:flex-none">
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      {loading ? (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-10 w-10 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Total Categories"
            value={totalCategories}
            icon={<FolderKanban className="h-6 w-6" />}
            iconClassName="bg-primary/10 text-primary"
          />
          <StatsCard
            title="Active Categories"
            value={activeCategories}
            icon={<CheckCircle2 className="h-6 w-6" />}
            iconClassName="bg-green-100 text-green-600"
          />
          <StatsCard
            title="Subcategories"
            value={categories.reduce((total, category) =>
              total + (category.subcategories?.length || 0), 0)}
            icon={<GitBranch className="h-6 w-6" />}
            iconClassName="bg-blue-100 text-blue-600"
          />
          <StatsCard
            title="Total Products"
            value={totalProducts}
            icon={<ShoppingBag className="h-6 w-6" />}
            iconClassName="bg-purple-100 text-purple-600"
          />
        </div>
      )}

      {/* Search and filters */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search categories..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {viewMode === 'hierarchy' && (
            <>
              <Button variant="outline" size="sm" onClick={expandAllCategories}>
                <ChevronDown className="h-4 w-4 mr-2" />
                Expand All
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAllCategories}>
                <ChevronRight className="h-4 w-4 mr-2" />
                Collapse All
              </Button>
            </>
          )}

          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Categories table */}
      {loading ? (
        <Card className="border-none shadow-sm">
          <CardContent className="p-0">
            <div className="border rounded-md relative overflow-hidden">
              <div className="overflow-auto max-h-[600px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted/50 z-10 border-b">
                    <TableRow>
                      <TableHead className="w-16">Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-center">Level</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-center">Products</TableHead>
                      <TableHead className="text-center">Pricing</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton className="h-12 w-12 rounded-md" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-48" />
                        </TableCell>
                        <TableCell className="text-center">
                          <Skeleton className="h-4 w-4 mx-auto" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell className="text-center">
                          <Skeleton className="h-4 w-8 mx-auto" />
                        </TableCell>
                        <TableCell className="text-center">
                          <Skeleton className="h-8 w-20 mx-auto" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-6 w-16" />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Skeleton className="h-8 w-8" />
                            <Skeleton className="h-8 w-8" />
                            <Skeleton className="h-8 w-8" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md border border-destructive/20 flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-destructive" />
          {error}
        </div>
      ) : (
        <Card className="border-none shadow-sm">
          <CardContent className="p-0">
            <div className="border rounded-md relative overflow-hidden">
              <div className="overflow-auto max-h-[600px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted/50 z-10 border-b">
                    <TableRow>
                      <TableHead className="w-16">Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-center">Level</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-center">Products</TableHead>
                      <TableHead className="text-center">Pricing</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCategories.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12">
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <FolderKanban className="h-12 w-12 mb-4 opacity-20" />
                            <p className="text-lg font-medium">No categories found</p>
                            <p className="text-sm opacity-80">Try adjusting your search or filters</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {viewMode === 'hierarchy' ? (
                          // Enhanced hierarchy view
                          <>
                            {filteredCategories.map(category =>
                              <React.Fragment key={category._id}>
                                {renderCategoryWithSubcategories(category)}
                              </React.Fragment>
                            )}
                          </>
                        ) : (
                          // Flat view - all categories in a single list
                          filteredCategories.map(category => renderCategoryRow(category))
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Category Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b shrink-0">
            <DialogTitle>Add New Category</DialogTitle>
            <DialogDescription>
              Create a new category to organize your products.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <ImageUpload
              onImageChange={setSelectedImage}
              label="Upload Category Image"
              className="w-full"
              previewClassName="w-32 h-32"
            />

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category-name">Name <span className="text-red-500">*</span></Label>
                <Input
                  id="category-name"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="e.g., Construction Materials"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category-description">Description</Label>
                <Input
                  id="category-description"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  placeholder="Brief description of the category"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="parent-category">Parent Category</Label>
                <Select value={newCategory.parent || "none"} onValueChange={(value) => setNewCategory({ ...newCategory, parent: value === "none" ? "" : value })}>
                  <SelectTrigger id="parent-category">
                    <SelectValue placeholder="Select parent category (Optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Top Level Category)</SelectItem>
                    {getParentCategories().map(category => (
                      <SelectItem key={category._id} value={category._id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-blue-900">Distance-based Pricing</Label>
                    <p className="text-xs text-blue-700">
                      Enable dynamic pricing based on customer location.
                    </p>
                  </div>
                  <Switch
                    checked={newCategory.useCalculationLogic}
                    onCheckedChange={(value) => setNewCategory({ ...newCategory, useCalculationLogic: value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className={`p-3 rounded-md border transition-colors ${newCategory.useCalculationLogic ? 'bg-blue-100 border-blue-200' : 'bg-background border-transparent'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Calculator className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-sm">Dynamic</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Calculated based on distance & transport fees.</p>
                  </div>
                  <div className={`p-3 rounded-md border transition-colors ${!newCategory.useCalculationLogic ? 'bg-blue-100 border-blue-200' : 'bg-background border-transparent'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-sm">Fixed</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Standard fixed pricing for all locations.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t bg-background mt-auto shrink-0">
            <Button variant="outline" onClick={() => {
              setIsAddModalOpen(false);
              setNewCategory({ name: '', description: '', parent: '', useCalculationLogic: true });
              setSelectedImage(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b shrink-0">
            <DialogTitle>Edit Category</DialogTitle>
            <DialogDescription>
              Update the category information.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {selectedCategory && (
              <>
                <ImageUpload
                  onImageChange={setEditImage}
                  currentImage={formatImageUrl(selectedCategory.image)}
                  label="Update Category Image"
                  className="w-full"
                  previewClassName="w-32 h-32"
                />

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-category-name">Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="edit-category-name"
                      value={selectedCategory.name}
                      onChange={(e) => setSelectedCategory({ ...selectedCategory, name: e.target.value })}
                      placeholder="Category name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-category-description">Description</Label>
                    <Input
                      id="edit-category-description"
                      value={selectedCategory.description}
                      onChange={(e) => setSelectedCategory({ ...selectedCategory, description: e.target.value })}
                      placeholder="Category description"
                    />
                  </div>

                  {selectedCategory.level !== 1 && (
                    <div className="space-y-2">
                      <Label htmlFor="edit-parent-category">Parent Category</Label>
                      <Select
                        value={selectedCategory.parent || 'none'}
                        onValueChange={(value) => setSelectedCategory({ ...selectedCategory, parent: value === 'none' ? null : value })}
                      >
                        <SelectTrigger id="edit-parent-category">
                          <SelectValue placeholder="Select parent category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None (Top Level Category)</SelectItem>
                          {getParentCategories()
                            .filter(cat => cat._id !== selectedCategory._id) // Cannot be its own parent
                            .map(category => (
                              <SelectItem key={category._id} value={category._id}>
                                {category.name}
                              </SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-3 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <Label className="text-sm font-medium text-blue-900">Distance-based Pricing</Label>
                        <p className="text-xs text-blue-700">
                          Enable dynamic pricing based on customer location.
                        </p>
                      </div>
                      <Switch
                        checked={selectedCategory.useCalculationLogic}
                        onCheckedChange={(value) => setSelectedCategory({ ...selectedCategory, useCalculationLogic: value })}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div className={`p-3 rounded-md border transition-colors ${selectedCategory.useCalculationLogic ? 'bg-blue-100 border-blue-200' : 'bg-background border-transparent'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Calculator className="h-4 w-4 text-blue-600" />
                          <span className="font-medium text-sm">Dynamic</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Calculated based on distance & transport fees.</p>
                      </div>
                      <div className={`p-3 rounded-md border transition-colors ${!selectedCategory.useCalculationLogic ? 'bg-blue-100 border-blue-200' : 'bg-background border-transparent'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className="h-4 w-4 text-blue-600" />
                          <span className="font-medium text-sm">Fixed</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Standard fixed pricing for all locations.</p>
                      </div>
                    </div>

                    {/* Configure Pricing Formula Button */}
                    {selectedCategory.useCalculationLogic && (
                      <div className="mt-4 pt-4 border-t border-blue-100">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                          onClick={() => {
                            setIsEditModalOpen(false);
                            // Add small delay to ensure cleanup of first modal finishes
                            // before second modal opens, preventing body lock race conditions
                            setTimeout(() => {
                              setIsPricingModalOpen(true);
                            }, 50);
                          }}
                        >
                          <Settings2 className="h-4 w-4 mr-2" />
                          Configure Pricing Formula
                        </Button>
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                          Customize per-km rate, minimum/maximum charges, and more
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="p-6 pt-4 border-t bg-background mt-auto shrink-0">
            <Button variant="outline" onClick={() => {
              setIsEditModalOpen(false);
              setEditImage(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateCategory} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Subcategories Modal */}
      <Dialog open={isViewSubcategoriesModalOpen} onOpenChange={setIsViewSubcategoriesModalOpen}>
        <DialogContent className="sm:max-w-4xl flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b shrink-0">
            <DialogTitle>Subcategories of {selectedCategory?.name}</DialogTitle>
            <DialogDescription>
              Manage subcategories for this category.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-6">
              <Button onClick={() => {
                if (selectedCategory) {
                  setNewCategory({
                    name: '',
                    description: '',
                    parent: selectedCategory._id,
                    useCalculationLogic: true
                  });
                  setIsViewSubcategoriesModalOpen(false);
                  setIsAddModalOpen(true);
                }
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Subcategory
              </Button>
            </div>

            {subcategories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 border rounded-lg bg-muted/5 border-dashed">
                <FolderTree className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">No subcategories found</h3>
                <p className="text-sm text-muted-foreground/80 mt-1">Add a subcategory to get started.</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow>
                      <TableHead className="w-16">Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subcategories.map(subcategory => (
                      <TableRow key={subcategory._id} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="h-10 w-10 rounded-md overflow-hidden border bg-muted">
                            {subcategory.image ? (
                              <img
                                src={formatImageUrl(subcategory.image)}
                                alt={subcategory.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <ImageIcon className="h-4 w-4 text-muted-foreground/50" />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{subcategory.name}</TableCell>
                        <TableCell className="text-muted-foreground">{subcategory.description || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={subcategory.isActive}
                              onCheckedChange={() => handleToggleStatus(subcategory)}
                              disabled={loading}
                              className="scale-75 data-[state=checked]:bg-green-600"
                            />
                            <Badge variant={subcategory.isActive ? 'success' : 'secondary'} className="text-xs px-2 py-0.5">
                              {subcategory.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-blue-50 hover:text-blue-600"
                              onClick={() => {
                                setSelectedCategory(subcategory);
                                setIsViewSubcategoriesModalOpen(false);
                                setIsEditModalOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-red-50 hover:text-red-600"
                              onClick={() => {
                                setSelectedCategory(subcategory);
                                setIsViewSubcategoriesModalOpen(false);
                                setIsDeleteModalOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter className="p-6 pt-4 border-t bg-background mt-auto shrink-0">
            <Button variant="outline" onClick={() => setIsViewSubcategoriesModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold">{selectedCategory?.name}</span>?
              This action cannot be undone and will also delete all subcategories.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteModalOpen(false)} disabled={loading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Products Modal */}
      <Dialog open={isProductsModalOpen} onOpenChange={setIsProductsModalOpen}>
        <DialogContent className="sm:max-w-6xl flex flex-col max-h-[90vh] p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b shrink-0">
            <DialogTitle>Products in {selectedCategory?.name}</DialogTitle>
            <DialogDescription>
              View all products assigned to this category.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6">
            {productsLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Loading products...</p>
              </div>
            ) : categoryProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 border rounded-lg bg-muted/5 border-dashed">
                <ShoppingBag className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No products found</p>
                <p className="text-sm text-muted-foreground/80 mt-1">This category has no products yet.</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow>
                      <TableHead className="w-16">Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Specifications</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryProducts.map(product => (
                      <TableRow key={product._id} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="h-12 w-12 rounded-md overflow-hidden border bg-muted">
                            {product.images && product.images.length > 0 ? (
                              <img
                                src={formatImageUrl(typeof product.images[0] === 'string'
                                  ? product.images[0]
                                  : (product.images[0] as any).url || '')}
                                alt={product.name}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=No+Image';
                                }}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <ImageIcon className="h-5 w-5 text-muted-foreground/50" />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="max-w-xs">
                          <div className="truncate text-sm text-muted-foreground" title={product.description}>
                            {product.description || 'No description'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">₹{product.basePrice.toLocaleString()}/{product.unit || 'unit'}</span>
                            {product.discount > 0 && (
                              <span className="text-xs text-green-600 font-medium">
                                {product.discount}% off
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {product.specifications && product.specifications.length > 0 ? (
                            <div className="text-xs space-y-1">
                              {product.specifications.slice(0, 2).map((spec, index) => (
                                <div key={index} className="flex gap-1">
                                  <span className="text-muted-foreground font-medium">{spec.name}:</span>
                                  <span>{spec.value}</span>
                                </div>
                              ))}
                              {product.specifications.length > 2 && (
                                <span className="text-xs text-muted-foreground italic">
                                  +{product.specifications.length - 2} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">No specifications</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={product.isActive !== false ? 'success' : 'secondary'} className="text-xs px-2 py-0.5">
                            {product.isActive !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter className="p-6 pt-4 border-t bg-background mt-auto shrink-0">
            <Button variant="outline" onClick={() => setIsProductsModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Configuration Modal */}
      {selectedCategory && (
        <PricingConfigModal
          isOpen={isPricingModalOpen}
          onClose={() => {
            setIsPricingModalOpen(false);
          }}
          categoryId={selectedCategory._id}
          categoryName={selectedCategory.name}
          onConfigUpdated={() => {
            fetchCategories();
          }}
        />
      )}
    </div>
  );
}